/**
 * Operator action logging (IMS "scribe" audit trail).
 *
 * Every operator interaction is logged with the COP context it happened in, so a
 * legal-discovery query can reconstruct what the operator saw. This is a
 * process-level singleton (not React state) so MapView's imperative Cesium
 * closures, the LayerToggle, and the side panel can all call it without prop
 * drilling. `useOperatorActions()` is a thin hook wrapper for React-idiomatic use.
 *
 * Fire-and-forget, NON-blocking, and silent: a queued action is POSTed on a 1 s
 * flush timer (so bursts — e.g. continuous pan/zoom — batch into one tick rather
 * than hammering the endpoint), via fetch with keepalive; errors never surface
 * to the caller. Logging must never affect the operator's experience.
 *
 * Durability (#253): an action is only dequeued once the api 2xx's it. During
 * the api boot/restart window the COP's audit writes fail in several shapes —
 * Vite-proxy 500 (api not listening yet), Fastify 503 (return503OnClosing
 * during the previous process's shutdown), connection reset (in-flight at
 * close), 429 (rate-limit burst) — and the old fire-and-forget client silently
 * lost the verb on every one of them. Now transient failures (network error,
 * any 5xx, 429) keep the action queued and the flush loop retries with
 * exponential backoff (2 s → 30 s cap) until the api accepts it; only
 * non-retryable 4xx responses drop an action (a rejected payload won't get
 * better by resending). MAX_QUEUE still bounds memory. Trade-off: the server
 * stamps `timestamp` at insert, so a retried boot verb is stamped late by the
 * retry delay — accepted; the pre-existing flush tick already skewed it ≤1 s.
 */

import type { OperatorActionType } from '@tsr/shared-types';

const SESSION_KEY = 'tsr.session';
const FLUSH_INTERVAL_MS = 1000;
/** Defensive client-side cap: drop the oldest queued actions past this so a
 *  runaway caller can't grow the queue without bound (server also rate-limits). */
const MAX_QUEUE = 500;
const ENDPOINT = '/api/operator-actions';

interface QueuedAction {
  action_type: OperatorActionType;
  session_id: string;
  payload?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

function newUuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // Fallback (older/test environments): RFC4122-ish v4 from Math.random.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

let sessionIdCache: string | null = null;

/** Stable per-session id, persisted to localStorage for the session lifetime. */
export function getSessionId(): string {
  if (sessionIdCache) return sessionIdCache;
  if (typeof window === 'undefined') {
    sessionIdCache = newUuid();
    return sessionIdCache;
  }
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) {
      sessionIdCache = existing;
      return existing;
    }
  } catch {
    /* storage blocked — fall through to in-memory id */
  }
  const id = newUuid();
  sessionIdCache = id;
  try {
    window.localStorage.setItem(SESSION_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

const queue: QueuedAction[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

/** Retry backoff after a transient send failure: first retry 2 s out, doubling
 *  to a 30 s ceiling so a down api isn't hammered for the whole outage. */
const BACKOFF_INITIAL_MS = 2_000;
const BACKOFF_MAX_MS = 30_000;

let inFlight = false;
let backoffMs = 0;
/** Epoch-ms gate: flush ticks are no-ops until this deadline after a failure. */
let retryAt = 0;

type SendOutcome = 'sent' | 'retry' | 'drop';

async function sendAction(a: QueuedAction): Promise<SendOutcome> {
  if (typeof fetch === 'undefined') return 'drop';
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(a),
      keepalive: true,
    });
    if (res.ok) return 'sent';
    // 5xx covers the whole boot/restart window (proxy 500/502/504 while the api
    // is down, Fastify 503 while it closes); 429 is the rate-limit backstop —
    // the window passes, so the verb is retryable, not lost.
    if (res.status === 429 || res.status >= 500) return 'retry';
    return 'drop';
  } catch {
    return 'retry'; // network-level failure (refused/reset) — api unreachable
  }
}

async function flush(): Promise<void> {
  if (inFlight || queue.length === 0) return;
  if (Date.now() < retryAt) return;
  inFlight = true;
  try {
    // Sequential head-of-queue sends preserve action order in the audit trail;
    // the first transient failure stops the pass and leaves the rest queued.
    while (queue.length > 0) {
      const outcome = await sendAction(queue[0]!);
      if (outcome === 'retry') {
        backoffMs = backoffMs === 0 ? BACKOFF_INITIAL_MS : Math.min(backoffMs * 2, BACKOFF_MAX_MS);
        retryAt = Date.now() + backoffMs;
        return;
      }
      queue.shift();
      backoffMs = 0;
      retryAt = 0;
    }
  } finally {
    inFlight = false;
  }
}

/** Unload path: awaiting is impossible, so fire the remainder best-effort with
 *  keepalive and drain the queue — the page is dying, so no retry pass will
 *  ever run, and re-sending on a bfcache restore would routinely duplicate
 *  verbs that the keepalive sends already landed. */
function flushOnPagehide(): void {
  if (typeof fetch === 'undefined') return;
  for (const a of queue) {
    try {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(a),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      /* swallow — logging must never throw into the caller */
    }
  }
  queue.length = 0;
}

function ensureTimer(): void {
  if (flushTimer || typeof window === 'undefined') return;
  flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  if (typeof window.addEventListener === 'function') {
    window.addEventListener('pagehide', flushOnPagehide);
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void flush();
    });
  }
}

/**
 * Queue one operator action. Non-blocking; flushed on the next 1 s tick.
 * @param action_type controlled vocabulary (see OperatorActionType)
 * @param payload     action-specific detail (which layer, which entity id, …)
 * @param context     COP snapshot at action time (bbox, toggle states, selection, zoom)
 */
export function logAction(
  action_type: OperatorActionType,
  payload?: Record<string, unknown>,
  context?: Record<string, unknown>,
): void {
  ensureTimer();
  if (queue.length >= MAX_QUEUE) queue.shift();
  queue.push({ action_type, session_id: getSessionId(), payload, context });
}

/** Test seam: run one flush pass (awaitable so tests can assert the outcome). */
export function __flushNow(): Promise<void> {
  return flush();
}

/** Test seam: clear the queue + retry/backoff state between cases. */
export function __resetForTests(): void {
  queue.length = 0;
  inFlight = false;
  backoffMs = 0;
  retryAt = 0;
}

export interface OperatorActionsApi {
  logAction: typeof logAction;
  sessionId: string;
}

/** React hook wrapper — returns the stable logger + the session id. */
export function useOperatorActions(): OperatorActionsApi {
  return { logAction, sessionId: getSessionId() };
}
